import { CheckCircle2, Lock, Landmark, MoreHorizontal, Mail, Wallet } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { UserAvatar } from "./UserAvatar";
import { rosterMembers, canRemovePoolMember, isPrimaryPoolOwner, isPoolCoOwner, recipientBadgeStatus } from "../lib/poolMembers";
import { formatBankMask } from "../lib/bankingLocale";

function MemberMenu({ member, canManage, canRemove, onSetBank, onRemove, onUploadAvatar }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", fn);
        return () => document.removeEventListener("mousedown", fn);
    }, []);
    return (
        <div className="relative" ref={ref}>
            <button
                onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
                className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 inline-flex items-center justify-center rounded-md hover:bg-white/[0.08]"
                data-testid={`recipient-menu-${member.id}`}
            >
                <MoreHorizontal className="h-4 w-4 text-white/60" />
            </button>
            {open && (
                <div className="absolute right-0 mt-1 w-48 z-30 rounded-lg border border-white/10 bg-[#1A1A1A] p-1 shadow-2xl">
                    {onUploadAvatar && (
                        <button
                            onClick={() => { setOpen(false); onUploadAvatar(member); }}
                            className="w-full text-left px-2.5 py-2 text-sm rounded-md hover:bg-white/[0.06]"
                            data-testid={`recipient-upload-avatar-${member.id}`}
                        >
                            Upload photo
                        </button>
                    )}
                    <button
                        onClick={() => { setOpen(false); onSetBank(member); }}
                        className="w-full text-left px-2.5 py-2 text-sm rounded-md hover:bg-white/[0.06]"
                        data-testid={`recipient-set-bank-${member.id}`}
                    >
                        {member.bank ? "Update bank account" : "Add bank account"}
                    </button>
                    {canManage && canRemove && (
                        <button
                            onClick={() => { setOpen(false); onRemove(member); }}
                            className="w-full text-left px-2.5 py-2 text-sm rounded-md hover:bg-white/[0.06] text-rose-400"
                            data-testid={`recipient-remove-${member.id}`}
                        >
                            Remove recipient
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export function Recipients({ members = [], pool, onHover, highlightId, canManage = false, onSetBank, onRemove, poolId, onMemberAvatarUpdated, scrollable = false, className = "" }) {
    const { user } = useAuth();
    const fileRef = useRef(null);
    const scrollRef = useRef(null);
    const [avatarTarget, setAvatarTarget] = useState(null);
    const [hasOverflow, setHasOverflow] = useState(false);

    const activeMembers = rosterMembers(members);

    useEffect(() => {
        if (!scrollable) {
            setHasOverflow(false);
            return undefined;
        }
        const el = scrollRef.current;
        if (!el) return undefined;

        const measure = () => {
            const overflow = el.scrollHeight > el.clientHeight + 2;
            setHasOverflow((prev) => {
                if (overflow) return true;
                if (!prev) return false;
                return el.scrollHeight > el.clientHeight - 8;
            });
        };

        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(el);
        Array.from(el.children).forEach((child) => observer.observe(child));
        return () => observer.disconnect();
    }, [scrollable, activeMembers.length, className]);

    const triggerAvatarUpload = (member) => {
        setAvatarTarget(member);
        fileRef.current?.click();
    };

    const onAvatarFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = "";
        if (!file || !avatarTarget || !poolId) return;
        const form = new FormData();
        form.append("file", file);
        try {
            const { data } = await api.post(`/pools/${poolId}/members/${avatarTarget.id}/avatar`, form);
            onMemberAvatarUpdated?.(data.pool);
            toast.success("Photo updated");
        } catch (err) {
            toast.error(err.response?.data?.detail || err.message || "Upload failed");
        }
        setAvatarTarget(null);
    };
    if (!activeMembers.length) {
        return (
            <div className="surface p-6 text-center text-white/45 text-sm" data-testid="recipients-empty">
                No members yet
            </div>
        );
    }
    const list = (
            <ul className="flex w-full min-w-0 flex-col gap-2.5" data-testid="recipients-list">
            {activeMembers.map((m) => {
                const active = highlightId === m.id;
                const hasBank = !!m.bank?.account_last4;
                const badge = recipientBadgeStatus(m);
                return (
                    <li
                        key={m.id}
                        onMouseEnter={() => onHover?.(m.id)}
                        onMouseLeave={() => onHover?.(null)}
                        className={`group relative w-full shrink-0 rounded-xl border border-white/[0.07] bg-[#232323] p-3 transition-all ${
                            active ? "border-[#00BF0D]/35 shadow-[0_0_0_1px_rgba(0,191,13,0.12)]" : "hover:border-white/15"
                        }`}
                        data-testid={`recipient-${m.id}`}
                    >
                        <div className="flex w-full items-start gap-2.5">
                            <div className="relative shrink-0">
                                <UserAvatar name={m.name} avatarUrl={m.avatar_url} className="h-10 w-10" accentColor={m.color} />
                                <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full ring-2 ring-[#232323]" style={{ background: m.color }} aria-hidden />
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex min-w-0 items-center gap-1.5 fs-pill-anchor">
                                    <p className="min-w-0 truncate font-medium leading-none text-white">
                                        {m.name}
                                        {m.user_id === user?.id && <span className="ml-1 text-[#00BF0D] text-xs font-semibold">(You)</span>}
                                        {isPoolCoOwner(m, pool) && m.user_id !== user?.id && (
                                            <span className="ml-1 text-[#00BF0D]/80 text-xs font-semibold">(Co-owner)</span>
                                        )}
                                    </p>
                                    {badge === "awaiting_invite" && (
                                        <span className="fs-pill shrink-0 gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300/90" data-testid={`recipient-pending-invite-${m.id}`}>
                                            <Mail className="h-3 w-3" /> Awaiting acceptance
                                        </span>
                                    )}
                                    {badge === "ready" && (
                                        <span className="fs-pill shrink-0 gap-1 rounded-full bg-[#00BF0D]/8 border border-[#00BF0D]/15 px-1.5 py-0.5 text-[10px] font-semibold text-[#00BF0D]" data-testid={`recipient-ready-${m.id}`}>
                                            <CheckCircle2 className="h-3 w-3" /> Ready for payout
                                        </span>
                                    )}
                                    {badge === "payout_setup" && (
                                        <span className="fs-pill shrink-0 gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300/90" data-testid={`recipient-payout-setup-${m.id}`}>
                                            <Wallet className="h-3 w-3" /> Payout setup needed
                                        </span>
                                    )}
                                    {badge === "needs_bank" && (
                                        <span className="fs-pill shrink-0 gap-1 rounded-full bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 text-[10px] font-semibold text-amber-300/90" data-testid={`recipient-needs-bank-${m.id}`}>
                                            <Landmark className="h-3 w-3" /> Needs bank account
                                        </span>
                                    )}
                                </div>
                                {m.role_label && <p className="mt-0.5 truncate text-xs text-white/45">{m.role_label}</p>}
                                {hasBank && formatBankMask(m) && (
                                    <div className="mt-1 flex items-center gap-1.5 text-[11px] leading-tight font-mono">
                                        <span className="inline-flex min-w-0 items-center gap-1 text-white/55">
                                            <Lock className="h-3 w-3 shrink-0 text-white/40" />
                                            <Landmark className="h-3 w-3 shrink-0 text-white/45" />
                                            <span className="truncate">{formatBankMask(m)}</span>
                                        </span>
                                    </div>
                                )}
                            </div>
                            <div className="flex shrink-0 items-start gap-1 pl-1">
                                <div className="min-w-[2.75rem] pt-0.5 text-right">
                                    <p className="font-heading text-lg leading-none tracking-tight font-mono tabular-nums" style={{ color: m.color }}>
                                        {m.percentage}%
                                    </p>
                                </div>
                                <div className="flex h-7 w-7 shrink-0 items-center justify-center">
                                    {(onSetBank || onRemove || poolId) && (
                                        <MemberMenu
                                            member={m}
                                            canManage={canManage}
                                            canRemove={canRemovePoolMember(m, pool, user?.id)}
                                            onSetBank={onSetBank}
                                            onRemove={onRemove}
                                            onUploadAvatar={
                                                poolId && (canManage || m.user_id === user?.id)
                                                    ? triggerAvatarUpload
                                                    : null
                                            }
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mt-2.5 h-1 w-full overflow-hidden rounded-full bg-white/[0.06]">
                            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${m.percentage}%`, background: m.color, boxShadow: isPrimaryPoolOwner(m, pool) || isPoolCoOwner(m, pool) ? "0 0 10px rgba(0,191,13,0.4)" : "none" }} />
                        </div>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden" onChange={onAvatarFile} />
            {scrollable ? (
                <div
                    ref={scrollRef}
                    className={`min-h-0 ${hasOverflow ? "overflow-y-auto overscroll-contain" : "overflow-visible"} ${className || "flex-1"}`}
                    data-testid="recipients-scroll"
                    data-scroll-overflow={hasOverflow ? "true" : "false"}
                >
                    {list}
                </div>
            ) : (
                list
            )}
        </>
    );
}
